import { useState } from 'react';
import { useLocation } from 'react-router-dom';

import Alert from '@mui/material/Alert';
import { AutocompleteCloseReason } from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Stack from '@mui/material/Stack';

import EllipsisText from '@webclient/components/ellipsis-typography';
import ListboxComponent from '@webclient/components/search-forms/gamification-search/actions/bots-selector/components/listbox';
import PaperComponent from '@webclient/components/search-forms/gamification-search/actions/bots-selector/components/paper';
import PopperComponent from '@webclient/components/search-forms/gamification-search/actions/bots-selector/components/popper';
import SkeletonLoading from '@webclient/components/search-forms/gamification-search/actions/bots-selector/components/skeleton';
import CategorySelector from '@webclient/components/search-forms/gamification-search/actions/bots-selector/sections/category-selector';
import {
  StyledAutocomplete,
  StyledBox,
  StyledCategoryStack,
  StyledEllipsis,
  StyledImageBot,
  StyledInput,
  StyledListItem,
  StyledOnlineDot,
  StyledPopper,
  StyledSelectorButton,
  StyledStackOptions,
  StyledStackOptionsItem,
  StyledText,
} from '@webclient/components/search-forms/gamification-search/actions/bots-selector/styles';

import calculateFullName from '@webclient/lib/calculateFullName';
import CONFIG from '@webclient/lib/const/bots';
import createPublicImageUrl, {
  retrieveImageWithFallback,
} from '@webclient/lib/createPublicImageUrl';
import getAvatar from '@webclient/lib/getAvatar';

import useAppDispatch from '@webclient/hooks/useAppDispatch';
import useAppSelector from '@webclient/hooks/useAppSelector';
import useGetBots from '@webclient/hooks/useGetBots';

import { setBotByUserId } from '@webclient/state/slices/app-settings';
import {
  selectBot,
  selectSearchMode,
} from '@webclient/state/slices/app-settings/userSettingSelector';
import { updateResultWithBotSwitch } from '@webclient/state/slices/search-session';

import {
  AllBotsCategoryType,
  BotsResponseType,
} from '@webclient/shared-types/bots';

import AtomRoundedIcon from '~icons/knowz-iconify/atom-rounded';
import SearchIcon from '~icons/knowz-iconify/search';

import { useTranslate } from '@tolgee/react';

const BOTS_AVATAR_FALLBACK =
  'https://asset.knowz.com/assets/images/not-found-characters-avatar-%5Boptimised%5D.webp';

export default function Autocomplete() {
  const { t } = useTranslate();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [category, setCategory] = useState<AllBotsCategoryType>('All');
  const dispatch = useAppDispatch();
  const me = useAppSelector((state) => state.auth.me);
  const bot = useAppSelector(selectBot);
  const searchMode = useAppSelector(selectSearchMode);
  const { pathname } = useLocation();
  const isItLiveSearchPage = pathname.includes('search');

  const {
    data: botsData,
    isLoading: isBotsDataLoading,
    isError: isBotsDataError,
  } = useGetBots({
    category:
      category === 'All' || category === 'Knowz AI' ? undefined : category,
    shouldPin: true,
  });

  const open = Boolean(anchorEl);

  function handleClick(event: React.MouseEvent<HTMLElement>) {
    setAnchorEl(event.currentTarget);
  }

  function handleClose() {
    if (anchorEl) {
      anchorEl.focus();
    }
    setAnchorEl(null);
  }

  if (isBotsDataError) {
    return (
      <Alert severity="error">
        {t('response.errors.code.shortCode.520', 'An error occurred')}
      </Alert>
    );
  }

  function handleFilterByCategory(value: AllBotsCategoryType) {
    setCategory(value);
  }

  return (
    <>
      <StyledSelectorButton
        disableRipple
        disableTouchRipple
        onClick={handleClick}
        size="small"
        loading={isBotsDataLoading}
        startIcon={searchMode === 'advanced' && <AtomRoundedIcon />}
      >
        <StyledEllipsis
          lineClamp={1}
          title={bot.name}
          variant="textSm"
        >
          {bot.name}
        </StyledEllipsis>
      </StyledSelectorButton>

      <StyledPopper
        open={open}
        anchorEl={anchorEl ?? null}
        modifiers={[
          {
            name: 'resetTransform',
            enabled: true,
            phase: 'beforeWrite',
            fn: (data) => {
              data.state.styles.popper.transform = 'unset';
              data.state.styles.popper.inset = 'auto auto 155px 0px';
            },
          },
        ]}
        placement="top"
        disablePortal
      >
        <ClickAwayListener onClickAway={handleClose}>
          <Box>
            <StyledAutocomplete
              open
              loading={isBotsDataLoading}
              loadingText={<SkeletonLoading count={3} />}
              onClose={(
                _event: React.ChangeEvent<unknown>,
                reason: AutocompleteCloseReason,
              ) => {
                if (reason === 'escape') {
                  handleClose();
                }
              }}
              value={bot}
              onChange={(event, newValue, reason) => {
                if (
                  event.type === 'keydown' &&
                  ((event as React.KeyboardEvent).key === 'Backspace' ||
                    (event as React.KeyboardEvent).key === 'Delete') &&
                  reason === 'removeOption'
                ) {
                  return;
                }
                const selectedBot = newValue as BotsResponseType[number] | null;
                if (selectedBot?.id === bot.id) return;
                if (selectedBot == null) return;
                if (!me) return;
                dispatch(
                  setBotByUserId({
                    userId: me.uuid,
                    bot: {
                      id: selectedBot.id,
                      name: selectedBot.name,
                      image: selectedBot.image,
                    },
                  }),
                );
                if (isItLiveSearchPage) {
                  const uuid = Date.now();
                  dispatch(
                    updateResultWithBotSwitch({
                      id: uuid,
                      from: {
                        id: bot.id,
                        name: bot.name,
                        image: bot.image,
                      },
                      to: {
                        id: selectedBot.id,
                        name: selectedBot.name,
                        image: selectedBot.image,
                      },
                    }),
                  );
                }
                handleClose();
              }}
              clearOnEscape
              renderTags={() => null}
              noOptionsText={
                <Alert severity="info">
                  {t(
                    'layout.searchWidget.botsSelector.noOptions',
                    "Couldn't find any bots with that name",
                  )}
                </Alert>
              }
              renderOption={(props, option, { selected, index }) => {
                const { key, ...optionProps } = props;
                const currentSelectedBot = option as BotsResponseType[number];
                const isItKnowzAI =
                  currentSelectedBot.id === CONFIG.DEFAULT_KNOWZ_AI_BOT.id;

                const isItMadeByKnowz = currentSelectedBot.isDefaultOwner;
                selected = currentSelectedBot.id === bot.id;

                const { name, category, description, owner, image } =
                  currentSelectedBot;

                return (
                  <StyledListItem
                    key={`${key}-${index}`}
                    disableGutters
                    disablePadding
                    selected={selected}
                    {...optionProps}
                  >
                    <StyledStackOptions>
                      <StyledBox>
                        {selected && <StyledOnlineDot />}
                        <StyledImageBot
                          src={
                            isItKnowzAI
                              ? CONFIG.DEFAULT_KNOWZ_AI_BOT.avatar
                              : retrieveImageWithFallback(
                                  createPublicImageUrl(getAvatar(image)),
                                  BOTS_AVATAR_FALLBACK,
                                )
                          }
                          alt={name}
                        />
                      </StyledBox>
                      <StyledStackOptionsItem>
                        <Stack
                          direction="row"
                          gap={3}
                        >
                          <EllipsisText
                            lineClamp={1}
                            variant="textMd"
                            weight="medium"
                          >
                            {name}
                          </EllipsisText>
                          <StyledCategoryStack>
                            {CONFIG.CATEGORY_BY_ICONS[category]}
                          </StyledCategoryStack>
                        </Stack>
                        <EllipsisText
                          variant="textSm"
                          lineClamp={1}
                        >
                          {description}
                        </EllipsisText>
                        <StyledText
                          variant="textXs"
                          sx={{ mt: 2 }}
                        >
                          {isItMadeByKnowz
                            ? 'By Knowz.com'
                            : `By ${calculateFullName({ firstName: owner?.firstName, lastName: owner?.lastName })}`}
                        </StyledText>
                      </StyledStackOptionsItem>
                    </StyledStackOptions>
                  </StyledListItem>
                );
              }}
              options={[...(botsData?.data || [])]?.sort((a, b) => {
                const ai = a.id;
                const bi = b.id;
                if (ai !== bot.id) return 1;
                if (bi !== bot.id) return -1;
                return 0;
              })}
              getOptionLabel={(option) =>
                (option as BotsResponseType[number]).name
              }
              renderInput={(params) => (
                <StyledInput
                  ref={params.InputProps.ref}
                  inputProps={params.inputProps}
                  autoFocus
                  startAdornment={<SearchIcon />}
                  placeholder={t(
                    'layout.searchWidget.botsSelector.placeholder',
                    'Find the personality that suites you',
                  )}
                  endAdornment={
                    <CategorySelector
                      currentSelectedCategory={category}
                      handleFilterByCategory={handleFilterByCategory}
                    />
                  }
                />
              )}
              slots={{
                popper: PopperComponent,
                listbox: ListboxComponent,
                paper: PaperComponent,
              }}
            />
          </Box>
        </ClickAwayListener>
      </StyledPopper>
    </>
  );
}
